import cx from 'classnames';
import Parser from 'html-react-parser';
import { isEmpty } from 'lodash';
import BasicEditor from 'pages/documents/misc/_BlockParser/BasicEditor';
import { RenderedInteractiveContent } from 'pages/documents_v2/views/edit_area/blocks/RenderedInteractiveContent';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useGetDocumentTags } from 'pages/tags/hooks/useGetDocumentTags';

export const RenderTable = ({
    baseBlock,
    columns = [],
    enableTableEditor = false,
    handleCellChange,
    highlightFirstColumn = false,
    modal = false,
    readOnly,
    rows = [],
    tableData,
    template,
    dirty = false,
    hideChanges = false,
}) => {
    const tags = useGetDocumentTags();
    const hideRowTitles = template?.properties?.hideRowTitles ?? false;
    const firstColumnTitle = template?.firstColumnTitle ?? '';
    const vertical = template?.editorOptions?.displayVertical ?? false;
    const sortedRows = [...rows].sort(HelperFunctions.dynamicSort('sortOrder'));

    if (vertical) {
        return (
            <RenderVerticalTable
                baseBlock={baseBlock}
                columns={columns}
                tags={tags}
                enableTableEditor={enableTableEditor}
                handleCellChange={handleCellChange}
                modal={modal}
                tableData={tableData}
                template={template}
                readOnly={readOnly}
                highlightFirstColumn={highlightFirstColumn}
                dirty={dirty}
                hideChanges={hideChanges}
            />
        );
    }

    return (
        <div className="table-responsive">
            <table className="table small mb-0">
                <thead>
                    <tr>
                        {hideRowTitles === false && (
                            <th
                                className={cx('font-weight-normal uk-width-1-4', {
                                    'bg-light': highlightFirstColumn,
                                })}
                            >
                                {Parser(firstColumnTitle)}
                            </th>
                        )}

                        {columns.map((column, colIndex) => {
                            return (
                                <th
                                    className={cx('font-weight-normal', {
                                        'bg-light': highlightFirstColumn && hideRowTitles && colIndex === 0,
                                    })}
                                    style={{ maxWidth: 250, width: 250 }}
                                    key={column.id}
                                >
                                    {Parser(column.name)}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => {
                        const rowData =
                            tableData === undefined ? [] : HelperFunctions.getByValue(tableData, 'id', row.id);
                        return (
                            <tr key={row.id}>
                                {hideRowTitles === false && Object.hasOwn(row, 'title') && (
                                    <td
                                        className={cx('text-uppercase', {
                                            'bg-light': highlightFirstColumn,
                                        })}
                                    >
                                        <span
                                            className={
                                                Object.hasOwn(row, 'parentId') && row.parentId !== null
                                                    ? 'uk-padding-left'
                                                    : ''
                                            }
                                        >
                                            {Parser(row.title)}
                                        </span>
                                    </td>
                                )}
                                {columns.map((column) => {
                                    const cellData = HelperFunctions.getByValue(
                                        rowData?.cells ?? [],
                                        'entityId',
                                        column.id,
                                    ) ?? {
                                        entityId: column.id,
                                        latestContent: '',
                                    };

                                    if (cellData !== undefined) {
                                        return (
                                            <td key={column.id}>
                                                <RenderTableCell
                                                    baseBlock={baseBlock}
                                                    columnId={column.id}
                                                    data={cellData}
                                                    tags={tags}
                                                    enableTableEditor={enableTableEditor}
                                                    toolbarSelector="#modal-editor-toolbar"
                                                    handleCellChange={handleCellChange}
                                                    rowId={row.id}
                                                    dirty={dirty}
                                                    hideChanges={hideChanges}
                                                />
                                            </td>
                                        );
                                    } else {
                                        return <td key={column.id} />;
                                    }
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

function RenderVerticalTable({
    baseBlock,
    columns = [],
    tags,
    enableTableEditor = false,
    handleCellChange,
    modal = false,
    tableData,
    template,
    readOnly,
    highlightFirstColumn = false,
    dirty = false,
    hideChanges = false,
}) {
    const hideRowTitles = template?.properties?.hideRowTitles ?? false;
    const columnCount = template.rows.length + 1;
    const headerWidth = (1 / columnCount) * 100;
    const cellWidth = 100 - headerWidth;

    return (
        <div
            className={cx({
                'uk-overflow-auto max-height-650vh': modal,
            })}
        >
            <div className="flex-table flex-table-striped flex-table-vertical text-black">
                <div className="flex-table-body">
                    {columns.map((column) => {
                        return (
                            <div className="flex-table-row" key={column.id}>
                                {hideRowTitles && (
                                    <div
                                        className={cx('flex-table-header', {
                                            'bg-light': highlightFirstColumn,
                                            'pt-4': enableTableEditor,
                                            'p-3': !enableTableEditor,
                                        })}
                                        style={{ width: `${headerWidth}%` }}
                                    >
                                        {Parser(column.name)}
                                    </div>
                                )}

                                {template.rows.map((_templateRow) => {
                                    const rowData =
                                        tableData === undefined
                                            ? undefined
                                            : HelperFunctions.getByValue(tableData, 'id', _templateRow.id);
                                    const rowCells = rowData?.cells ?? undefined;
                                    let cellData = rowCells
                                        ? HelperFunctions.getByValue(rowCells, 'entityId', column.id)
                                        : undefined;

                                    if (!cellData) {
                                        cellData = {
                                            entityId: column.id,
                                            latestContent: '',
                                        };
                                    }

                                    return (
                                        <div
                                            className={cx('flex-table-cell', {
                                                'flex-table-cell-editor pt-3': enableTableEditor,
                                                'p-3': !enableTableEditor,
                                            })}
                                            style={{ width: `${cellWidth}%` }}
                                            key={`${column.id}-${_templateRow.id}`}
                                        >
                                            <RenderTableCell
                                                baseBlock={baseBlock}
                                                columnId={column.id}
                                                data={cellData}
                                                tags={tags}
                                                enableTableEditor={enableTableEditor}
                                                handleCellChange={handleCellChange}
                                                rowId={_templateRow.id}
                                                readOnly={readOnly}
                                                toolbarSelector="#modal-editor-toolbar"
                                                dirty={dirty}
                                                hideChanges={hideChanges}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function RenderTableCell({
    baseBlock,
    columnId,
    data,
    enableTableEditor,
    handleCellChange,
    rowId,
    readOnly,
    tags = [],
    toolbarSelector,
    dirty = false,
    hideChanges = false,
}) {
    if (
        baseBlock &&
        baseBlock.changes.length > 0 &&
        !hideChanges &&
        Object.hasOwn(data, 'diffContent') &&
        !isEmpty(data.diffContent) &&
        !dirty &&
        !readOnly
    ) {
        return <RenderedInteractiveContent block={baseBlock} content={data.diffContent} />;
    }

    if (enableTableEditor && !readOnly) {
        return (
            <BasicEditor
                id={'jsTinymceEditor' + rowId + columnId}
                name={'jsTinymceEditor' + rowId + columnId}
                toolbarSelector={toolbarSelector}
                onChange={(event) => handleCellChange(rowId, columnId, event.target.value)}
                tags={tags}
                value={data.latestContent}
            />
        );
    }

    if (data !== undefined) {
        let html = '';

        if (Object.hasOwn(data, 'renderedDiffContent') && data.renderedDiffContent !== null) {
            html = data.renderedDiffContent;
        } else {
            html = data.latestContent ?? '';
        }

        return <>{Parser(html)}</>;
    }

    return null;
}
